'use client';

import React, { useEffect, useState } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Minus, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FAQJsonLD } from '@/lib/seo/faq-schema';

type PicStudioFaqItem = {
  question: string;
  answerRichText?: any;
  tags?: string[]; // Optional tags for filtering
  group?: string; // FAQ group name
};

interface FAQProps {
  tag?: string; // Optional tag to filter FAQs
  variant?: 'card' | 'collapsible'; // Controls rendering style
}

const FAQ: React.FC<FAQProps> = ({ tag, variant = 'card' }) => {
  const [faqs, setFaqs] = useState<PicStudioFaqItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  useEffect(() => {
    const fetchFAQs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Include tag in the API request if provided
        const url = tag ? `/api/faq?tag=${encodeURIComponent(tag)}` : '/api/faq';
        console.log(`FAQ: Fetching FAQs from API with URL: ${url}`);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('FAQ: API response received', data);

        // Check if data has the expected structure
        if (!data.entries || !Array.isArray(data.entries) || data.entries.length === 0) {
          console.error('FAQ: Invalid data structure - entries is missing or empty', data);
          throw new Error('No FAQ entries found');
        }

        // Extract FAQ items from the transformed API response
        const entry = data.entries[0];
        if (!entry?.fields?.picStudioFaqItem || !Array.isArray(entry.fields.picStudioFaqItem)) {
          console.error('FAQ: Invalid entry structure', entry);
          throw new Error('Invalid FAQ data structure');
        }

        const faqItems = entry.fields.picStudioFaqItem
          .map((item: any) => {
            if (!item.fields || !item.fields.question) {
              console.warn('FAQ: Skipping item with missing question field', item);
              return null;
            }
            return {
              question: item.fields.question,
              answerRichText: item.fields.answerRichText || null,
              tags: item.fields.tags || [],
              group: item.fields.group || null
            };
          })
          .filter(Boolean);

        console.log(`FAQ: Successfully processed ${faqItems.length} FAQ items`);
        setFaqs(faqItems);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load FAQs';
        setError(errorMessage);
        console.error('FAQ: Error fetching FAQs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFAQs();
  }, [tag]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-pulse text-base">Loading FAQs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="text-red-500 text-base">Error: {error}</div>
      </div>
    );
  }

  if (faqs.length === 0) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="text-gray-500 text-base">{tag ? `No FAQs found for "${tag}"` : 'No FAQs available'}</div>
      </div>
    );
  }

  const renderCardVariant = () => (
    <div className="grid grid-cols-1 gap-6">
      {faqs.map((faq, i) => (
        <Card key={`faq-${i}`}>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{faq.question}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-base leading-7">
              {faq.answerRichText ? (
                documentToReactComponents(faq.answerRichText)
              ) : (
                <p className="text-gray-500 italic">No answer provided.</p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderCollapsibleVariant = () => (
    <dl className="space-y-6 divide-y divide-white/10">
      {faqs.map((faq, i) => {
        const isOpen = openItems.has(i);
        return (
          <div key={`faq-${i}`} className="pt-6">
            <dt>
              <button
                type="button"
                className="flex w-full items-start justify-between text-left hover:bg-gray-50/5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg p-2 -m-2 transition-colors"
                onClick={() => toggleItem(i)}
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${i}`}
              >
                <span className="text-base font-semibold">{faq.question}</span>
                <span className="ml-6 flex h-7 items-center flex-shrink-0">
                  {isOpen ? (
                    <Minus className="h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Plus className="h-6 w-6" aria-hidden="true" />
                  )}
                </span>
              </button>
            </dt>
            <dd
              id={`faq-answer-${i}`}
              className={`mt-2 pr-12 overflow-hidden transition-all duration-300 ease-in-out ${
                isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
              aria-hidden={!isOpen}
            >
              <div className="text-base leading-7 pt-2">
                {faq.answerRichText ? (
                  documentToReactComponents(faq.answerRichText)
                ) : (
                  <p className="text-gray-500 italic">No answer provided.</p>
                )}
              </div>
            </dd>
          </div>
        );
      })}
    </dl>
  );

  return (
    <>
      {/* JSON-LD structured data for SEO */}
      <FAQJsonLD
        faqs={faqs.map(faq => ({
          question: faq.question,
          answerRichText: faq.answerRichText
        }))}
        pageUrl={typeof window !== 'undefined' ? window.location.href : undefined}
      />

      <div className="flex flex-col md:flex-row gap-6 lg:gap-20 py-10">
        <div className="w-full md:w-1/3 space-y-4">
          <div className="text-accent">Frequently asked questions</div>
          <h2 className="text-2xl lg:text-4xl text-foreground font-bold">Everything you need to know</h2>
          {tag && (
            <div className="text-sm text-gray-600">
              Showing {faqs.length} FAQ{faqs.length !== 1 ? 's' : ''} for "{tag}"
            </div>
          )}
        </div>
        <div className="w-full">{variant === 'collapsible' ? renderCollapsibleVariant() : renderCardVariant()}</div>
      </div>
    </>
  );
};

export default FAQ;
